import { Component, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { PolicyRepoService } from '@client/app/services/api/policy-repo/policy-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { FormGlobalPolicyComponent } from '@client/app/forms/global-policy/form-global-policy.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';

@Component({
    selector: 'pages-policy',
    standalone: false,
    template: `
        <page-header [icon]="['POLICIES', 'DASHBOARD'] | monIcons"
                     [header]="'Content Policies Summary' | translate"
                     [subHeader]="'Find and address violations relating to your content guides and regulatory compliance and find outdated content across your website.' | translate">
            <page-header-content>
                <div class="row">
                    <div class="col-48 text-right">
                        <button class="btn btn-border-radius btn-secondary"
                                type="button"
                                (click)="openPolicyForm()"
                                monEventTrigger="actionTrigger"
                                [monEventParams]="{action:'before-policy-create'}"
                                translate>
                            Add new policy
                        </button>
                    </div>
                </div>
            </page-header-content>
        </page-header>

        <div class="row policy-dashboard">
            <div class="col-md-24">
                <div class="card panel-monsido-card">
                    <div class="card-body">
                        <mon-spinner [progress]="progress">
                            <div class="col-48 inner-none" *ngIf="!loading">
                                <p class="text-center font-size-1-5" translate>Priorities</p>
                                <p class="text-center text-secondary-4 inner-l-medium inner-r-medium" translate>
                                    Distribution of policies with matches across priority levels
                                </p>
                                <div class="col-48 px-12" *ngIf="!loading && policies">
                                    <policy-priority [policies]="policies"></policy-priority>
                                </div>
                            </div>
                        </mon-spinner>
                    </div>
                </div>

                <div class="card panel-monsido-card">
                    <div class="card-body">
                        <mon-spinner [progress]="progress">
                            <div class="col-48 inner-none" *ngIf="!loading">
                                <p class="text-center font-size-1-5" translate>Policy Distribution</p>
                                <p class="text-center text-secondary-4 inner-l-medium inner-r-medium" translate>
                                    Distribution of policies that matches their corresponding setting
                                </p>
                                <div class="col-48 inner-r-extra-large inner-l-extra-large">
                                    <policy-distribution [policies]="policies" *ngIf="!loading && policies"></policy-distribution>
                                </div>
                            </div>
                        </mon-spinner>
                    </div>
                </div>
            </div>
            <div class="col-md-24">
               <div class="card panel-monsido-card">
                   <div class="card-header">
                       <h2 class="font-weight-300 font-size-1-5 mb-1" translate>
                           Policy Diagnostics
                       </h2>
                       <span class="text-grey" translate>Percentage shows number of pages that are compliant with all Policies</span>
                   </div>
                   <div class="card-body">
                       <mon-spinner [progress]="progress">
                           <policy-last-scan *ngIf="history && isNumber(pagesErrorsCount) && isNumber(pagesCount) && policies"
                                             [policies]="policies"
                                             [pagesErrorsCount]="pagesErrorsCount"
                                             [pagesCount]="pagesCount"
                                             [linksCount]="linksCount"
                                             [history]="history">
                           </policy-last-scan>
                       </mon-spinner>
                   </div>
                   <div class="card-footer bg-white text-right" *ngIf="showHistory">
                       <a class="btn-link" [uiSref]="'base.customer.domain.changesHistory'" [uiParams]="historyCenterParameters"
                          monEventTrigger="actionTrigger"
                          [monEventParams]="{action:'go-to-history-center', from: 'domain-policy-dashboard'}"
                          translate>
                           Show history
                       </a>
                   </div>
               </div>
            </div>
        </div>
    `,
})
export class PagesPolicyComponent implements OnInit, OnDestroy {
    historyCenterParameters: any = {};
    policies: any[] = [];
    loading = true;
    destroyed = false;
    domain: any;
    progress: any = null;
    pagesErrorsCount: number = 0;
    pagesCount: number = 0;
    linksCount: number = 0;
    history: any[] = [];
    showHistory = false;

    constructor (
        private policyRepoService: PolicyRepoService,
        private crawlsRepoService: CrawlsRepoService,
        private stateService: StateService,
        private monEventService: MonEventService,
        private sessionService: SessionService,
        private pageRepoService: PageRepoService,
        private linksRepoService: LinksRepoService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
    ) {}

    ngOnInit (): void {
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('policy');
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');

        const promises: Promise<any>[] = [];
        promises.push(this.getPolicyPages());
        promises.push(this.getPages());
        promises.push(this.getLinks());
        promises.push(this.getPolicies());
        promises.push(this.getDomainHistory());

        this.loading = true;
        this.destroyed = false;
        this.domain = this.sessionService.domain;
        this.policies = [];

        this.progress = Promise.all(promises).then(($values: any[]) => {
            this.loading = false;
            this.pagesErrorsCount = parseInt($values[0].total);
            this.pagesCount = parseInt($values[1].total);
            this.linksCount = parseInt($values[2].total);
        }).catch(() => {
            // Handle errors gracefully like angular.noop
        });
    }

    ngOnDestroy (): void {
        this.destroyed = true;
    }

    private getPolicies (): Promise<any> {
        const params = {
            page_size: 0,
        };

        return this.policyRepoService.domainGetAll(params).then((policies: any) => {
            this.policies = policies.sort((a: any, b: any) => {
                return a.matches_count > b.matches_count ? -1 : a.matches_count < b.matches_count ? 1 : 0;
            });
        }).catch(() => {
            // Handle errors gracefully like angular.noop
        });
    }

    private getPolicyPages (): Promise<any> {
        const params = {
            page_size: 1,
            page: 1,
        };
        return this.policyRepoService.domainGetPages(params);
    }

    private getPages (): Promise<any> {
        const params = {
            page_size: 1,
            page: 1,
        };
        return this.pageRepoService.getAll(params);
    }

    private getLinks (): Promise<any> {
        const params = {
            page: 1,
            page_size: 1,
            page_fixed: false,
        };
        return this.linksRepoService.getAll(params);
    }

    private getDomainHistory (): Promise<any> {
        const params = {
            page_size: 5,
        };

        return this.crawlsRepoService.getAll(params).then((data: any) => {
            this.history = data !== undefined ? data.reverse() : [];
        }).catch(() => {
            // Handle errors gracefully like angular.noop
        });
    }

    openPolicyForm (): void {
        const params = {
            body: FormGlobalPolicyComponent,
            size: 'fw',
        };
        const callback = (payload: any) => {
            if (payload?.$value) {
                this.monUIRouterTransitionService.onTransitionFinished(
                    () => {
                        this.stateService.go('base.customer.domain.policy.policies');
                    },
                );
            }
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
    }

    isNumber (value: any): boolean {
        return typeof value === 'number' && !isNaN(value);
    }
}
